import type { UTCDate } from '@date-fns/utc';

import type {
  ReviewFilters,
  ReviewSecondaryFilters,
  ReviewType,
} from '@revelio/data-access';
import { SelectionCategories } from '@revelio/iso-utility';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../shared/selection-lists/types';
import type { RoleLeafIds } from '../hooks/useRoleLeafIds';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { isDateRangeFilter, isTreeFilter } from './common';
import { getNumericIdFromNestedId } from './get-numeric-id-from-nested-id';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';

type SelectedEntity = {
  id: number;
  label: string;
  selectionListId: AllSelectionListsWithSkillTaxonomyKey;
};

export class MissingRoleLeafIdsError extends Error {
  constructor() {
    super('No data available for the selected roles.');
    this.name = 'MissingRoleLeafIdsError';
  }
}

const getIndustryIndex = ({
  id,
  selectionListId,
}: {
  selectionListId: 'rics_k10' | 'rics_k400';
  id: number;
}) => {
  switch (selectionListId) {
    case 'rics_k10': {
      return { RICS_K10_INDEX: [id] };
    }
    case 'rics_k400': {
      return { RICS_K400_INDEX: [id] };
    }
  }
};

const getGeographyIndex = ({
  id,
  selectionListId,
}: {
  selectionListId: 'region' | 'country' | 'state' | 'county' | 'msa';
  id: number;
}) => {
  switch (selectionListId) {
    case 'region':
      return { REGION_INDEX: [id] };
    case 'country':
      return { COUNTRY_INDEX: [id] };
    case 'state':
      return { STATE_INDEX: [id] };
    case 'county':
      return { COUNTY_INDEX: [id] };
    case 'msa':
      return { MSA_INDEX: [id] };
  }
};

const extractMsaIdFromNestedCityId = (nestedId: string): number | null => {
  const parts = nestedId.split('_');
  // For nested ids like region_country_msa_city, the MSA id is the penultimate part
  const maybeMsaId = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  const msaId = Number(maybeMsaId);
  return Number.isFinite(msaId) ? msaId : null;
};

const BASE_YEAR = 2008;
const BASE_MONTH = 1;
const MAX_MONTHS = 12;

const getMonthIndex = (date: UTCDate): number => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  return (year - BASE_YEAR) * 12 + (month - BASE_MONTH);
};

const createIndices = (from: number, count: number) =>
  Array.from({ length: count }, (_, i) => from - i).filter(
    (index) => index >= 0
  );

const getMonthIndices = (
  filter: DateRangeFilter,
  isSnapshotDate = false
): number[] => {
  const { min: startDate, max: endDate } = filter;

  if (!startDate || !endDate) return [];

  const referenceDate = isSnapshotDate ? startDate : endDate;
  const monthCount = (() => {
    if (isSnapshotDate) return MAX_MONTHS;

    const startIndex = getMonthIndex(startDate);
    const endIndex = getMonthIndex(endDate);
    return Math.min(endIndex - startIndex + 1, MAX_MONTHS);
  })();

  return createIndices(getMonthIndex(referenceDate), monthCount);
};

const getIndexFilters = (
  filters: Filter[],
  isSnapshotDate = true
): ReviewSecondaryFilters => {
  return filters.reduce((acc, filter): ReviewSecondaryFilters => {
    if (filter?.id === FilterId.ROLE && isTreeFilter(filter)) {
      const roleK7Indices = filter.selections
        ?.filter((entity) => entity?.levelId === 'role_k7')
        ?.map((entity) => Number(entity?.id));

      const roleK150Indices = filter.selections
        ?.filter((entity) => entity?.levelId === 'role_k150')
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        ?.map((entity) => parseInt(entity.id.split('_').slice(-1)?.[0] ?? ''));

      const roleK1500Indices = filter.selections
        ?.filter((entity) => entity?.levelId === 'role_k1500')
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        ?.map((entity) => parseInt(entity.id.split('_').slice(-1)?.[0] ?? ''));

      const roleK15000Indices = filter.selections
        ?.filter((entity) => entity?.levelId === 'role_k15000')
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        ?.map((entity) => parseInt(entity.id.split('_').slice(-1)?.[0] ?? ''));

      return {
        ...acc,
        ...(roleK7Indices &&
          roleK7Indices.length && { ROLE_K7_V3_INDEX: roleK7Indices }),
        ...(roleK150Indices &&
          roleK150Indices.length && { ROLE_K150_V3_INDEX: roleK150Indices }),
        ...(roleK1500Indices &&
          roleK1500Indices.length && { ROLE_K1500_V3_INDEX: roleK1500Indices }),
        ...(roleK15000Indices &&
          roleK15000Indices.length && {
            ROLE_K15000_V3_INDEX: roleK15000Indices,
          }),
      };
    }

    if (
      (filter?.id === FilterId.COMPANY || filter?.id === FilterId.INDUSTRY) &&
      isTreeFilter(filter)
    ) {
      // Company and Industry both land here, so union their ids rather than
      // letting whichever is reduced last replace the other's.
      const { company, rics_k10, rics_k400 } = getV2CompanyFilters(filter);
      return {
        ...acc,
        ...(company &&
          company.length && {
            RCID: [...(acc.RCID ?? []), ...company],
          }),
        ...(rics_k10 &&
          rics_k10.length && {
            RICS_K10_INDEX: [...(acc.RICS_K10_INDEX ?? []), ...rics_k10],
          }),
        ...(rics_k400 &&
          rics_k400.length && {
            RICS_K400_INDEX: [...(acc.RICS_K400_INDEX ?? []), ...rics_k400],
          }),
      };
    }

    if (filter?.id === FilterId.GEOGRAPHY && isTreeFilter(filter)) {
      const { region, country, state, msa } = getV2GeographyFilters(filter);
      // County is read straight off the selections: `getV2GeographyFilters`
      // builds V2 query filters, and no V2 input declares `county`, so teaching
      // it that level would leak an unsupported field into V2 payloads.
      const county =
        filter.selections
          ?.filter((entity) => entity?.levelId === 'county')
          ?.map((entity) => getNumericIdFromNestedId(entity.id))
          ?.filter((id): id is number => id !== null) ?? [];
      // TEMPORARY: until CITY is supported in the sentiment review payload
      // Derive parent MSA ids from any selected cities (when cities are present in selections)
      const msaFromCitySelections =
        filter.selections
          ?.filter((entity) => entity?.levelId === 'city')
          ?.map((entity) => extractMsaIdFromNestedCityId(entity.id))
          ?.filter((id): id is number => id !== null) ?? [];
      const combinedMsa = Array.from(
        new Set([...(msa ?? []), ...msaFromCitySelections])
      );

      return {
        ...acc,
        ...(region && region.length && { REGION_INDEX: region }),
        ...(country && country.length && { COUNTRY_INDEX: country }),
        ...(state && state.length && { STATE_INDEX: state }),
        ...(county.length && { COUNTY_INDEX: county }),
        ...(combinedMsa.length && { MSA_INDEX: combinedMsa }),
      };
    }

    if (
      filter?.id === FilterId.SNAPSHOT_DATE &&
      filter?.type === FilterType.DATE
    ) {
      return acc;
    }

    if (filter?.id === FilterId.DATE_RANGE && isDateRangeFilter(filter)) {
      if (isSnapshotDate || !filter.min || !filter.max) {
        return acc;
      }

      const monthIndices = getMonthIndices(filter, isSnapshotDate);

      return {
        ...acc,
        MONTH_INDEX: monthIndices,
      };
    }

    if (filter?.id === FilterId.SENIORITY && filter.type === FilterType.LIST) {
      return {
        ...acc,
        SENIORITY: filter.selections?.map((selection) => Number(selection.id)),
      };
    }

    if (
      filter?.id === FilterId.OVERALL_RATING &&
      filter.type === FilterType.STATIC_LIST
    ) {
      return {
        ...acc,
        RATING_OVERALL: filter.selections?.map((selection) =>
          Number(selection.id)
        ),
      };
    }

    return acc;
  }, {});
};

interface GenerateSentimentPayloadProps {
  filters: Filter[];
  roleLeafIds: RoleLeafIds | undefined;
  primaryFilter: PrimaryFilterId;
  reviewType: ReviewType;
  selectedEntity: SelectedEntity;
  isSnapshotDate: boolean;
}

export const generateSentimentPayload = ({
  filters,
  roleLeafIds,
  primaryFilter,
  reviewType,
  selectedEntity,
  isSnapshotDate,
}: GenerateSentimentPayloadProps):
  | ReviewFilters
  | MissingRoleLeafIdsError
  | null => {
  const { id, label, selectionListId } = selectedEntity;

  const allIndexFilters = getIndexFilters(filters, isSnapshotDate);

  // If primary filter is ROLE, ensure the selected role is included in the mapping
  const indexFiltersWithPrimaryRole = (() => {
    if (primaryFilter !== FilterId.ROLE) return allIndexFilters;

    const roleKeyMap: Record<string, keyof ReviewSecondaryFilters> = {
      role_k7: 'ROLE_K7_V3_INDEX',
      role_k150: 'ROLE_K150_V3_INDEX',
      role_k1500: 'ROLE_K1500_V3_INDEX',
      role_k15000: 'ROLE_K15000_V3_INDEX',
    };

    const roleKey = roleKeyMap[selectionListId];
    if (!roleKey) return allIndexFilters;

    const roleId =
      selectionListId === 'role_k7'
        ? Number(id)
        : // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
          parseInt(id.toString().split('_').slice(-1)?.[0] ?? '');

    const existingRoles = (allIndexFilters[roleKey] as number[]) || [];
    if (existingRoles.includes(roleId)) return allIndexFilters;

    return {
      ...allIndexFilters,
      [roleKey]: [...existingRoles, roleId],
    };
  })();

  const filtersWithMappedRoles = getMappedRoleIndex({
    reviewFilters: indexFiltersWithPrimaryRole,
    roleLeafIds,
  });
  if (
    !filtersWithMappedRoles ||
    filtersWithMappedRoles instanceof MissingRoleLeafIdsError
  ) {
    return filtersWithMappedRoles;
  }

  switch (primaryFilter) {
    case FilterId.INDUSTRY:
    case FilterId.COMPANY: {
      if (selectionListId === SelectionCategories.COMPANY) {
        const {
          RICS_K10_INDEX: _RICS_K10_INDEX,
          RICS_K50_INDEX: _RICS_K50_INDEX,
          RICS_K400_INDEX: _RICS_K400_INDEX,
          RCID: _RCID,
          ...filtersWithoutRics
        } = filtersWithMappedRoles;

        return {
          COMPANY_NAME: label,
          ...filtersWithoutRics,
          RCID: [id],
          reviewType,
        };
      }
      if (selectionListId === 'rics_k10' || selectionListId === 'rics_k400') {
        const ricsIndex = getIndustryIndex({ id, selectionListId });
        const {
          RICS_K10_INDEX: _RICS_K10_INDEX,
          RICS_K50_INDEX: _RICS_K50_INDEX,
          RICS_K400_INDEX: _RICS_K400_INDEX,
          RCID: _RCID,
          ...filtersWithoutRics
        } = filtersWithMappedRoles;

        return {
          INDUSTRY_NAME: label,
          ...filtersWithoutRics,
          ...ricsIndex,
          reviewType,
        };
      }

      return null;
    }
    case FilterId.GEOGRAPHY: {
      if (
        selectionListId === 'region' ||
        selectionListId === 'country' ||
        selectionListId === 'state' ||
        selectionListId === 'county' ||
        selectionListId === 'msa'
      ) {
        const {
          REGION_INDEX: _REGION_INDEX,
          COUNTRY_INDEX: _COUNTRY_INDEX,
          STATE_INDEX: _STATE_INDEX,
          COUNTY_INDEX: _COUNTY_INDEX,
          MSA_INDEX: _MSA_INDEX,
          ...filtersWithoutGeo
        } = filtersWithMappedRoles;

        return {
          GEOGRAPHY_NAME: label,
          ...getGeographyIndex({ id, selectionListId }),
          ...filtersWithoutGeo,
          reviewType,
        };
      }
      if (selectionListId === 'city') {
        // When a city is selected as the primary geography, include its parent MSA index
        // until CITY is supported in the sentiment review payload
        const {
          REGION_INDEX: _REGION_INDEX,
          COUNTRY_INDEX: _COUNTRY_INDEX,
          STATE_INDEX: _STATE_INDEX,
          COUNTY_INDEX: _COUNTY_INDEX,
          ...filtersWithoutRegionCountryState
        } = filtersWithMappedRoles;

        return {
          GEOGRAPHY_NAME: label,
          // MSA_INDEX already includes parent MSAs from city selections (computed in getIndexFilters)
          ...filtersWithoutRegionCountryState,
          reviewType,
        } as ReviewFilters;
      }

      return null;
    }
    case FilterId.ROLE: {
      const validRoleSelections = [
        'role_k7',
        'role_k150',
        'role_k1500',
        'role_k15000',
      ];
      if (!validRoleSelections.includes(selectionListId)) return null;

      // Extract only K17000 indices and exclude all other role indices
      const { ROLE_K17000_V3_INDEX } = filtersWithMappedRoles;
      const filtersWithoutAnyRoles = Object.fromEntries(
        Object.entries(filtersWithMappedRoles).filter(
          ([key]) => !key.includes('ROLE_') && !key.includes('MAPPED_ROLE')
        )
      );

      return {
        ROLE_NAME: label,
        ROLE_K17000_V3_INDEX,
        ...filtersWithoutAnyRoles,
        reviewType,
      } as ReviewFilters;
    }
    // Sentiment reviews have no skill or activity support; those primaries
    // request none.
    case FilterId.CUSTOM_SKILL:
    case FilterId.ACTIVITY:
      return null;
  }
};

const getMappedRoleIndex = ({
  reviewFilters,
  roleLeafIds,
}: {
  reviewFilters: ReviewSecondaryFilters;
  roleLeafIds: RoleLeafIds | undefined;
}): ReviewSecondaryFilters | MissingRoleLeafIdsError | null => {
  const {
    ROLE_K7_V3_INDEX: roleK7Index,
    ROLE_K150_V3_INDEX: roleK150Index,
    ROLE_K1500_V3_INDEX: roleK1500Index,
    ROLE_K15000_V3_INDEX: roleK15000Index,
    ...restReviewFilters
  } = reviewFilters;

  const roleIndicesByLevel = [
    ['role_k7', roleK7Index],
    ['role_k150', roleK150Index],
    ['role_k1500', roleK1500Index],
    ['role_k15000', roleK15000Index],
  ] as const;

  const hasRoleFilter = roleIndicesByLevel.some(
    ([, roleIndex]) => !!roleIndex?.length
  );
  if (!hasRoleFilter) return restReviewFilters;
  if (!roleLeafIds) return null;

  const mappedRolesSet = new Set<number>();
  for (const [levelId, roleIndex] of roleIndicesByLevel) {
    const leafIdsByRoleId = roleLeafIds.get(levelId);
    for (const roleId of roleIndex ?? []) {
      for (const leafId of leafIdsByRoleId?.get(roleId) ?? []) {
        mappedRolesSet.add(leafId);
      }
    }
  }

  if (mappedRolesSet.size === 0) return new MissingRoleLeafIdsError();

  return {
    ...restReviewFilters,
    ROLE_K17000_V3_INDEX: Array.from(mappedRolesSet),
  };
};
